import { useState, useEffect } from 'react'
import { Card, Form, Button, Alert, Spinner, Row, Col } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import api from '../../services/api'

const BudgetConfig = ({ budget }) => {
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState({
    activo: true,
    frecuencia_calculo: 'mensual',
    alerta_porcentaje_80: true,
    alerta_porcentaje_95: true,
    bloqueo_100: false,
    alerta_desviacion: false,
    umbral_desviacion: 10
  })

  // Obtener configuración del presupuesto
  const { data: config, isLoading, error } = useQuery(
    ['budget-config', budget?.cliente],
    async () => {
      const response = await api.get(`/financial/configuraciones-presupuesto/?cliente_id=${budget.cliente}`)
      const results = response.data.results || response.data || []
      return results.length > 0 ? results[0] : null
    },
    { enabled: !!budget?.cliente }
  )

  // Actualizar formData cuando se carga la configuración
  useEffect(() => {
    if (config) {
      setFormData({
        activo: config.activo ?? true,
        frecuencia_calculo: config.frecuencia_calculo || 'mensual',
        alerta_porcentaje_80: config.alerta_porcentaje_80 ?? true,
        alerta_porcentaje_95: config.alerta_porcentaje_95 ?? true,
        bloqueo_100: config.bloqueo_100 ?? false,
        alerta_desviacion: config.alerta_desviacion ?? false,
        umbral_desviacion: parseFloat(config.umbral_desviacion || 10)
      })
    }
  }, [config])

  // Mutación para guardar configuración
  const saveMutation = useMutation(
    async (data) => {
      if (config?.id) {
        return api.put(`/financial/configuraciones-presupuesto/${config.id}/`, data)
      } else {
        return api.post('/financial/configuraciones-presupuesto/', {
          ...data,
          cliente: budget.cliente
        })
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['budget-config', budget?.cliente])
        toast.success('Configuración guardada exitosamente')
      },
      onError: (error) => {
        const message = error.response?.data?.error || error.response?.data?.message || 'Error al guardar configuración'
        toast.error(message)
      }
    }
  )

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) : value)
    }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    saveMutation.mutate(formData)
  }

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Cargando configuración...</p>
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="danger">
        Error al cargar configuración: {error.message}
      </Alert>
    )
  }

  return (
    <Card>
      <Card.Header>
        <h5 className="mb-0">Configuración de Alertas y Cálculo</h5>
      </Card.Header>
      <Card.Body>
        <Form onSubmit={handleSubmit}>
          {/* Configuración General */}
          <Card className="mb-3">
            <Card.Header>
              <h6 className="mb-0">Configuración General</h6>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      id="activo"
                      name="activo"
                      label="Activar cálculo automático de ejecución"
                      checked={formData.activo}
                      onChange={handleChange}
                    />
                    <Form.Text className="text-muted">
                      Si está activo, el sistema calculará la ejecución automáticamente según la frecuencia configurada.
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Frecuencia de Cálculo</Form.Label>
                    <Form.Select
                      name="frecuencia_calculo"
                      value={formData.frecuencia_calculo}
                      onChange={handleChange}
                      disabled={!formData.activo}
                    >
                      <option value="diario">Diario</option>
                      <option value="semanal">Semanal</option>
                      <option value="mensual">Mensual</option>
                      <option value="trimestral">Trimestral</option>
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Con qué frecuencia se calculará la ejecución del presupuesto.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Alertas de Ejecución */}
          <Card className="mb-3">
            <Card.Header>
              <h6 className="mb-0">Alertas de Ejecución</h6>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      id="alerta_porcentaje_80"
                      name="alerta_porcentaje_80"
                      label="Alerta al 80% de ejecución"
                      checked={formData.alerta_porcentaje_80}
                      onChange={handleChange}
                    />
                    <Form.Text className="text-muted">
                      Se generará una alerta cuando el porcentaje de ejecución alcance el 80%.
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      id="alerta_porcentaje_95"
                      name="alerta_porcentaje_95"
                      label="Alerta al 95% de ejecución"
                      checked={formData.alerta_porcentaje_95}
                      onChange={handleChange}
                    />
                    <Form.Text className="text-muted">
                      Se generará una alerta cuando el porcentaje de ejecución alcance el 95%.
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      id="bloqueo_100"
                      name="bloqueo_100"
                      label="Bloquear transacciones al 100%"
                      checked={formData.bloqueo_100}
                      onChange={handleChange}
                    />
                    <Form.Text className="text-muted">
                      Si está activo, se bloquearán las transacciones cuando el presupuesto supere el 100% de ejecución.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Alertas de Desviación */}
          <Card className="mb-3">
            <Card.Header>
              <h6 className="mb-0">Alertas de Desviación</h6>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      id="alerta_desviacion"
                      name="alerta_desviacion"
                      label="Activar alertas por desviación significativa"
                      checked={formData.alerta_desviacion}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Umbral de Desviación (%)</Form.Label>
                    <Form.Control
                      type="number"
                      name="umbral_desviacion"
                      value={formData.umbral_desviacion}
                      onChange={handleChange}
                      min="0"
                      max="100"
                      step="0.01"
                      disabled={!formData.alerta_desviacion}
                    />
                    <Form.Text className="text-muted">
                      Se generará una alerta cuando la desviación entre presupuestado y ejecutado supere este porcentaje.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <div className="d-flex justify-content-end">
            <Button
              type="submit"
              variant="primary"
              disabled={saveMutation.isLoading}
            >
              {saveMutation.isLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Guardando...
                </>
              ) : (
                'Guardar Configuración'
              )}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  )
}

export default BudgetConfig
